import React, { useState, useEffect } from 'react';
import type { DiaryItem, Supplement } from '../models/diary';
import { diaryService, ExistingSupplement } from '../services/diaryService';

const SupplementItemEditForm: React.FC<{ item: DiaryItem; onChange: (field: string, value: any) => void }> = ({ item, onChange }) => {
  // State for existing items from database
  const [existingSupplements, setExistingSupplements] = useState<ExistingSupplement[]>([]);
  const [loadingExistingItems, setLoadingExistingItems] = useState(false);
  
  // State for supplement selection mode
  const [supplementSelectionMode, setSupplementSelectionMode] = useState<'existing' | 'new'>('existing');
  const [selectedExistingSupplementIds, setSelectedExistingSupplementIds] = useState<number[]>([]);

  // Fetch existing supplements when component mounts
  useEffect(() => {
    const fetchExistingSupplements = async () => {
      setLoadingExistingItems(true);
      try {
        const supplements = await diaryService.getExistingSupplements();
        setExistingSupplements(supplements);
        
        // Initialize selected existing supplement IDs based on current supplements
        const existingIds = (item.supplements || [])
          .filter(supplement => supplement.id && supplements.some(es => es.id === supplement.id))
          .map(supplement => supplement.id!);
        setSelectedExistingSupplementIds(existingIds);
      } catch (error) {
        console.error('Error fetching existing supplements:', error);
      } finally {
        setLoadingExistingItems(false);
      }
    };

    fetchExistingSupplements();
  }, [item.supplements]);
  // Helper functions for supplement management
  const handleSupplementChange = (supplementIndex: number, field: keyof Supplement, value: string) => {
    const updatedSupplements = [...(item.supplements || [])];
    updatedSupplements[supplementIndex] = {
      ...updatedSupplements[supplementIndex],
      [field]: value
    };
    onChange('supplements', updatedSupplements);
  };

  const addSupplement = () => {
    const newSupplement: Supplement = {
      id: Date.now(), // Temporary ID for new supplements
      name: '',
      description: '',
      shopUrl: ''
    };
    const updatedSupplements = [...(item.supplements || []), newSupplement];
    onChange('supplements', updatedSupplements);
  };

  const removeSupplement = (supplementIndex: number) => {
    const updatedSupplements = [...(item.supplements || [])];
    updatedSupplements.splice(supplementIndex, 1);
    onChange('supplements', updatedSupplements);
  };

  // Helper functions for existing supplement selection
  const handleExistingSupplementSelection = (supplementId: number) => {
    const existingSupplement = existingSupplements.find(s => s.id === supplementId);
    if (!existingSupplement) return;

    // Convert existing supplement to Supplement format
    const supplement: Supplement = {
      id: existingSupplement.id,
      name: existingSupplement.name,
      description: existingSupplement.description,
      shopUrl: existingSupplement.shop_url || undefined
    };

    const updatedSupplements = [...(item.supplements || []), supplement];
    onChange('supplements', updatedSupplements);
    setSelectedExistingSupplementIds(prev => [...prev, supplementId]);
  };

  const removeExistingSupplement = (supplementId: number) => {
    const updatedSupplements = (item.supplements || []).filter(s => s.id !== supplementId);
    onChange('supplements', updatedSupplements);
    setSelectedExistingSupplementIds(prev => prev.filter(id => id !== supplementId));
  };

  return (
  <>
    <label>Nombre
      <input value={item.name} onChange={e => onChange('name', e.target.value)} style={{ width: '100%', marginBottom: 10 }} />
    </label>
    <label>Día de la semana
      <select value={item.dayOfWeek} onChange={e => onChange('dayOfWeek', e.target.value)} style={{ width: '100%', marginBottom: 10 }}>
        <option value="Monday">Lunes</option>
        <option value="Tuesday">Martes</option>
        <option value="Wednesday">Miércoles</option>
        <option value="Thursday">Jueves</option>
        <option value="Friday">Viernes</option>
        <option value="Saturday">Sábado</option>
        <option value="Sunday">Domingo</option>
      </select>
    </label>
    <label>Momento del día
      <select value={item.timeOfDay} onChange={e => onChange('timeOfDay', e.target.value)} style={{ width: '100%', marginBottom: 10 }}>
        <option value="Morning">Mañana</option>
        <option value="Afternoon">Tarde</option>
        <option value="Evening">Noche</option>
        <option value="Other">Otro</option>
      </select>
    </label>
    <label>Orden
      <input type="number" value={item.order} onChange={e => onChange('order', parseInt(e.target.value))} style={{ width: '100%', marginBottom: 10 }} min="1" />
    </label>
    <label style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
      <input type="checkbox" checked={item.hidden || false} onChange={e => onChange('hidden', e.target.checked)} style={{ marginRight: 8 }} />
      Oculto
    </label>

    {/* Supplements Section */}
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <label style={{ fontWeight: 600, margin: 0 }}>Suplementos</label>
      </div>

      {/* Supplement Selection Mode Toggle */}
      <div style={{ marginBottom: 10 }}>
        <label style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
          <input 
            type="radio" 
            name="supplementMode" 
            checked={supplementSelectionMode === 'existing'} 
            onChange={() => setSupplementSelectionMode('existing')}
            style={{ marginRight: 6 }}
          />
          <span style={{ fontSize: 14 }}>Seleccionar de existentes</span>
        </label>
        <label style={{ display: 'flex', alignItems: 'center' }}>
          <input 
            type="radio" 
            name="supplementMode" 
            checked={supplementSelectionMode === 'new'} 
            onChange={() => setSupplementSelectionMode('new')}
            style={{ marginRight: 6 }}
          />
          <span style={{ fontSize: 14 }}>Crear nuevo suplemento</span>
        </label>
      </div>

      {/* Existing Supplements Selection */}
      {supplementSelectionMode === 'existing' && (
        <div style={{ marginBottom: 10 }}>
          <label style={{ display: 'block', marginBottom: 4 }}>
            <span style={{ fontSize: 13, fontWeight: 500 }}>Seleccionar suplemento:</span>
            {loadingExistingItems ? (
              <div style={{ padding: 8, fontSize: 13, color: '#666' }}>Cargando suplementos...</div>
            ) : (
              <select 
                onChange={e => e.target.value && handleExistingSupplementSelection(parseInt(e.target.value))}
                style={{ width: '100%', marginTop: 2, padding: 4, border: '1px solid #ccc', borderRadius: 3, fontSize: 13 }}
                value=""
              >
                <option value="">Seleccionar un suplemento</option>
                {existingSupplements
                  .filter(supplement => !selectedExistingSupplementIds.includes(supplement.id))
                  .map(supplement => (
                    <option key={supplement.id} value={supplement.id}>
                      {supplement.name}
                    </option>
                  ))
                }
              </select>
            )}
          </label>
        </div>
      )}

      {/* New Supplement Creation */}
      {supplementSelectionMode === 'new' && (
        <div style={{ marginBottom: 10 }}>
          <button 
            type="button" 
            onClick={addSupplement}
            style={{ 
              background: '#38b2ac', 
              color: '#fff', 
              border: 'none', 
              borderRadius: 4, 
              padding: '4px 8px', 
              fontSize: 12, 
              cursor: 'pointer',
              marginBottom: 8
            }}
          >
            + Agregar Nuevo Suplemento
          </button>
        </div>
      )}

      {/* Display Selected/Created Supplements */}
      {(item.supplements || []).map((supplement, index) => {
        const isExisting = supplement.id && selectedExistingSupplementIds.includes(supplement.id);
        return (
          <div key={supplement.id || index} style={{ 
            border: '1px solid #e0e0e0', 
            borderRadius: 6, 
            padding: 8, 
            marginBottom: 8, 
            backgroundColor: isExisting ? '#f0f9ff' : '#f9f9f9' 
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ fontSize: 14, fontWeight: 600 }}>
                {isExisting ? `Suplemento Existente: ${supplement.name}` : `Suplemento ${index + 1}`}
              </span>
              <button 
                type="button" 
                onClick={() => isExisting ? removeExistingSupplement(supplement.id!) : removeSupplement(index)}
                style={{ 
                  background: '#ef4444', 
                  color: '#fff', 
                  border: 'none', 
                  borderRadius: 4, 
                  padding: '2px 6px', 
                  fontSize: 12, 
                  cursor: 'pointer' 
                }}
              >
                Eliminar
              </button>
            </div>
            
            {isExisting ? (
              // Read-only display for existing supplements
              <div>
                <div style={{ fontSize: 13, marginBottom: 4 }}>
                  <strong>Descripción:</strong> {supplement.description}
                </div>
                {supplement.shopUrl && (
                  <div style={{ fontSize: 13, marginBottom: 4 }}>
                    <strong>URL de compra:</strong> 
                    <a href={supplement.shopUrl} target="_blank" rel="noopener noreferrer" style={{ marginLeft: 4, color: '#38b2ac' }}>
                      {supplement.shopUrl}
                    </a>
                  </div>
                )}
              </div>
            ) : (
              // Editable fields for new supplements
              <>
                <label style={{ display: 'block', marginBottom: 4 }}>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>Nombre:</span>
                  <input 
                    value={supplement.name} 
                    onChange={e => handleSupplementChange(index, 'name', e.target.value)}
                    style={{ width: '100%', marginTop: 2, padding: 4, border: '1px solid #ccc', borderRadius: 3, fontSize: 13 }} 
                  />
                </label>
                
                <label style={{ display: 'block', marginBottom: 4 }}>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>Descripción:</span>
                  <textarea 
                    value={supplement.description} 
                    onChange={e => handleSupplementChange(index, 'description', e.target.value)}
                    style={{ width: '100%', marginTop: 2, padding: 4, border: '1px solid #ccc', borderRadius: 3, fontSize: 13, minHeight: 40, resize: 'vertical' }} 
                  />
                </label>
                
                <label style={{ display: 'block', marginBottom: 4 }}>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>URL de compra (opcional):</span>
                  <input 
                    value={supplement.shopUrl || ''} 
                    onChange={e => handleSupplementChange(index, 'shopUrl', e.target.value)}
                    style={{ width: '100%', marginTop: 2, padding: 4, border: '1px solid #ccc', borderRadius: 3, fontSize: 13 }} 
                    placeholder="https://..."
                  />
                </label>
              </>
            )}
          </div>
        );
      })}
    </div>
  </>
);
};

export default SupplementItemEditForm; 